import React, { useState, useCallback } from 'react';
import { Modal, Input, Form, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface ExportModalProps {
  onExport: (email?: string) => Promise<void>;
  isLoading?: boolean;
  buttonText?: string;
  modalTitle?: string;
  modalDescription?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  optionalNote?: string;
  buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  buttonSize?: 'small' | 'middle' | 'large';
  showIcon?: boolean;
  className?: string;
  disabled?: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({
  onExport,
  isLoading = false,
  buttonText,
  modalTitle,
  modalDescription,
  emailLabel,
  emailPlaceholder,
  optionalNote,
  buttonType = 'primary',
  buttonSize = 'large',
  showIcon = true,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const handleOpenModal = useCallback(() => {
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    form.resetFields();
  }, [form]);

  const handleExport = useCallback(
    async (values: { email?: string }) => {
      try {
        await onExport(values.email);
        handleCloseModal();
      } catch (error) {
        // Error handling is done in the parent component
        console.error('Export error:', error);
      }
    },
    [onExport, handleCloseModal]
  );

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      handleExport(values);
    });
  }, [form, handleExport]);

  return (
    <>
      <Button
        type={buttonType}
        size={buttonSize}
        icon={showIcon ? <DownloadOutlined /> : undefined}
        onClick={handleOpenModal}
        loading={isLoading}
        className={className}
        disabled={disabled}
      >
        {buttonText || t('common.export')}
      </Button>

      <Modal
        title={modalTitle || t('common.exportModal.title')}
        open={modalVisible}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        confirmLoading={isLoading}
        okText={t('common.exportModal.exportButton')}
        cancelText={t('common.cancel')}
        width={500}
      >
        <div className="mb-3">
          <p className="text-muted">{modalDescription || t('common.exportModal.description')}</p>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label={emailLabel || t('common.exportModal.emailLabel')}
            rules={[
              {
                type: 'email',
                message: t('common.exportModal.emailInvalid'),
              },
            ]}
          >
            <Input
              placeholder={emailPlaceholder || t('common.exportModal.emailPlaceholder')}
              size="large"
            />
          </Form.Item>

          <div className="alert alert-info">
            <small>{optionalNote || t('common.exportModal.optionalNote')}</small>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ExportModal;
